import React, { useState } from 'react';
import styles from './styles.module.scss';
import { ConsentState, ProductType } from '../../types';
import SavingsSummary from '../SavingsSummary';
import { trackCallButtonClick, trackAppDownloadClick } from '../../utils/tracking';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

interface SuccessSlideProps {
  productType: ProductType;
  zipFlag: boolean | null;
  zipCode?: string;
  mongoId?: string;
  phone?: string;
  consents: ConsentState;
  onConsentsChange: (consents: ConsentState) => void;
}

/**
 * Slide di successo dopo l'invio del form
 */
const SuccessSlide: React.FC<SuccessSlideProps> = ({
  productType,
  zipFlag,
  zipCode,
  mongoId,
  phone,
  consents,
  onConsentsChange,
}) => {
  const [editingPartner, setEditingPartner] = useState(false);

  const handleCallClick = () => {
    trackCallButtonClick();
  };

  const handleAppDownloadClick = () => {
    trackAppDownloadClick();
  };

  return (
    <div className={styles.successSlide}>
      <div className={styles.successHeader}>
        <div className={styles.successCheckmark}>✓</div>
      </div>

      <div className={styles.successCtaBar}>
        <a
          href="tel:00393476331490"
          className={styles.btnCallNow}
          onClick={handleCallClick}
        >
          Chiamaci Ora
        </a>
      </div>

      <h3 className={styles.successMainTitle}>
        Approfitta subito dei Bonus, ti chiamiamo a breve da un numero di
        cellulare.
      </h3>

      <p className={styles.successSubtitle}>
        Alternativamente puoi contattarci già tu cliccando su "Chiamaci Ora".
      </p>

      <SavingsSummary productType={productType} zipFlag={zipFlag} zipCode={zipCode} />

      <section className={styles.preferences}>
        <h4 className={styles.preferencesTitle}>Le tue preferenze</h4>
        <p className={styles.preferencesHint}>Facoltativi, revocabili in ogni momento</p>

        <div className={styles.preferenceActions}>
          <button
            type="button"
            className={styles.preferenceBulk}
            onClick={() =>
              onConsentsChange({
                ...consents,
                enpalMarketingConsent: false,
                thirdPartyProfiling: false,
              })
            }
          >
            Deseleziona tutti
          </button>
          <button
            type="button"
            className={styles.preferenceBulk}
            onClick={() =>
              onConsentsChange({
                ...consents,
                enpalMarketingConsent: true,
                thirdPartyProfiling: true,
              })
            }
          >
            Seleziona tutti
          </button>
        </div>

        <label className={styles.preferenceCard}>
          <span className={styles.preferenceCardTop}>
            <span className={styles.preferenceName}>Offerte e novità Enpal</span>
            <input
              type="checkbox"
              className={styles.preferenceCheckbox}
              checked={consents.enpalMarketingConsent}
              onChange={() =>
                onConsentsChange({
                  ...consents,
                  enpalMarketingConsent: !consents.enpalMarketingConsent,
                })
              }
            />
          </span>
          <span className={styles.preferenceText}>
            Acconsento a ricevere offerte e novità da Enpal Italia via email, SMS, WhatsApp e
            telefono.
          </span>
        </label>

        <label className={styles.preferenceCard}>
          <span className={styles.preferenceCardTop}>
            <span className={styles.preferenceName}>Proposte su misura (profilazione)</span>
            <input
              type="checkbox"
              className={styles.preferenceCheckbox}
              checked={consents.thirdPartyProfiling}
              onChange={() =>
                onConsentsChange({
                  ...consents,
                  thirdPartyProfiling: !consents.thirdPartyProfiling,
                })
              }
            />
          </span>
          <span className={styles.preferenceText}>
            Acconsento che Enpal Italia usi consumi, dati dell&apos;immobile e interazioni per
            propormi offerte su misura.
          </span>
        </label>

        <div className={styles.partnerRow}>
          <span>
            Dati ai partner: <strong>{consents.thirdPartyMarketing ? 'Sì' : 'No'}</strong>
          </span>
          <button
            type="button"
            className={styles.modifyLink}
            onClick={() => setEditingPartner((open) => !open)}
          >
            Modifica
          </button>
        </div>

        {editingPartner && (
          <label className={styles.preferenceCard}>
            <span className={styles.preferenceCardTop}>
              <span className={styles.preferenceName}>Comunicazione dei dati ai partner</span>
              <input
                type="checkbox"
                className={styles.preferenceCheckbox}
                checked={consents.thirdPartyMarketing}
                onChange={() =>
                  onConsentsChange({
                    ...consents,
                    thirdPartyMarketing: !consents.thirdPartyMarketing,
                  })
                }
              />
            </span>
            <span className={styles.preferenceText}>
              Acconsento a comunicare i miei dati ai partner in{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                elenco
              </a>
              , per ricevere le loro offerte. Agiscono come titolari autonomi.
            </span>
          </label>
        )}
      </section>

      {mongoId && (
        <a
          href={`https://app.enpal.it/login?payload=${btoa(phone || '')}`}
          className={styles.appCta}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleAppDownloadClick}
        >
          Scarica l'app per scoprire di più sui bonus
        </a>
      )}
    </div>
  );
};

export default SuccessSlide;
